import type { TobisAdmissionsPoint } from "./tobis";

export interface AdmissionsChartPoint {
  /** Achsenbeschriftung, "10/2018" bzw. "2018". */
  label: string;
  /** Der dargestellte Wert (kumuliert oder je Periode, siehe AdmissionsChartData.mode). */
  value: number;
  /** Besucher in dieser Periode, fuer den Tooltip. */
  periodValue: number | null;
  rank: number | null;
}

export interface AdmissionsChartData {
  points: AdmissionsChartPoint[];
  /** "total" = kumuliert seit Kinostart, "period" = Wert je Periode. */
  mode: "total" | "period";
  /** Aufloesung der X-Achse, aus dem ersten Punkt uebernommen. */
  resolution: "month" | "year";
}

/**
 * Bereitet den Besucherverlauf fuer die Darstellung auf.
 *
 * Bevorzugt wird die kumulierte Reihe (admissionsTotal), weil die FFA-Listen
 * RANGLISTEN sind und keine Vollerhebung: ein Film faellt aus der Top 100 und
 * taucht Monate spaeter wieder auf. Die Periodenwerte haben deshalb Luecken.
 * Der kumulierte Wert kommt von der FFA selbst und traegt die Luecken
 * korrekt ueber - eine eigene Summe aus den bekannten Perioden waere
 * systematisch zu niedrig und damit eine erfundene Zahl.
 *
 * Beleg: "Bohemian Rhapsody" fehlt in den Monatslisten April und Mai 2020
 * (Kinoschliessungen), der kumulierte Wert springt von 3.852.141 auf
 * 3.910.205 - die 58.064 Besucher dazwischen stehen in keiner Zeile.
 *
 * Rueckfall auf die Periodenwerte nur, wenn weniger als zwei Punkte einen
 * kumulierten Wert tragen: dann gaebe die Kurve keine Linie her. Betrifft
 * vor allem Titel, die nur in einer Arthouse-Jahresliste stehen - die fuehrt
 * als einzige keinen Gesamtwert.
 */
export function admissionsChartData(points: TobisAdmissionsPoint[]): AdmissionsChartData | null {
  if (points.length === 0) return null;

  const resolution = points[0]?.periodType === "month" ? "month" : "year";
  const withTotal = points.filter((p) => p.admissionsTotal != null);
  const mode: "total" | "period" = withTotal.length >= 2 ? "total" : "period";
  const quelle = mode === "total" ? withTotal : points.filter((p) => p.admissionsPeriod != null);

  const chartPoints = quelle.map((p) => ({
    label: periodLabel(p.periodStart, resolution),
    value: (mode === "total" ? p.admissionsTotal : p.admissionsPeriod) ?? 0,
    periodValue: p.admissionsPeriod,
    rank: p.bestRank,
  }));

  // Ein einzelner Punkt ist keine Kurve. Die Zahl selbst steht ohnehin schon
  // in der Kennzahlenliste der Detailseite, ein Diagramm daraus waere
  // Dekoration ohne Aussage.
  if (chartPoints.length < 2) return null;

  return { points: chartPoints, mode, resolution };
}

/**
 * "2018-10-01" -> "10/2018" bzw. "2018".
 *
 * Bewusst ohne Intl.DateTimeFormat: die Achse braucht eine kurze, in jeder
 * Sprache gleich lesbare Marke, kein lokalisiertes Monatswort ("Okt." waere
 * je nach Sprache unterschiedlich breit und liesse die Achse springen).
 */
export function periodLabel(periodStart: string, resolution: "month" | "year"): string {
  const [jahr, monat] = periodStart.split("-");
  if (!jahr) return periodStart;
  return resolution === "month" && monat ? `${monat}/${jahr}` : jahr;
}
